import type { ExistingSystemComponentType } from 'data/types';
import {
  COMPONENT_TYPE_TO_POSITION,
  ExistingEquipmentPositionSlot,
  keptEquipmentPositionSlots
} from 'utils/existing-equipment';
import type { KeptBlower } from 'utils/open-slot-placeholder-labels';

export type CompatibilityPositionType = 'outdoor' | 'indoor' | 'blower';

export type CompatibilityPosition = {
  type: CompatibilityPositionType;
  existingEquipmentComponentUniqueId: string | null;
  replacing: boolean;
  label: string;
};

export type CompatibilityPositionPlan = {
  positions: CompatibilityPosition[];
  keptComponentLabels: string[];
  keptBlower: KeptBlower | null;
  keptSlots: ReadonlySet<ExistingEquipmentPositionSlot>;
};

type SlotOccupancy = {
  outdoor: unknown;
  indoor: unknown;
  blower: unknown;
};

const NEW_ADDITION_LABELS: Record<CompatibilityPositionType, string> = {
  outdoor: 'Outdoor Unit',
  indoor: 'Indoor Unit',
  blower: 'Blower/Furnace'
};

const SLOT_TYPES: CompatibilityPositionType[] = ['outdoor', 'indoor', 'blower'];

// Spec fields live loosely on the EE component object (they aren't in the
// shared zod schema — same access pattern as readComponentTonnage in
// tonnage-logic): coerce and validate here. Null = no usable capacity, the
// renderers show "—" instead of a number.
export function readComponentHeatingCapacity(
  component: { [key: string]: unknown } | null
): number | null {
  const raw = component?.heatingCapacity;
  if (raw == null || raw === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function positionsFromComponents(
  components: ExistingSystemComponentType[]
): CompatibilityPosition[] {
  return components.flatMap((component) => {
    const position = COMPONENT_TYPE_TO_POSITION[component.type];
    if (!position) return [];
    return [
      {
        type: position,
        existingEquipmentComponentUniqueId: component.uniqueId,
        replacing: component.replacing,
        label: component.type
      }
    ];
  });
}

function newAdditionPositions(
  positions: CompatibilityPosition[],
  combos: SlotOccupancy[]
): CompatibilityPosition[] {
  const occupied = new Set(positions.map((position) => position.type));
  const additions: CompatibilityPosition[] = [];
  combos.forEach((combo) => {
    SLOT_TYPES.forEach((slot) => {
      if (combo[slot] && !occupied.has(slot)) {
        additions.push({
          type: slot,
          existingEquipmentComponentUniqueId: null,
          replacing: true,
          label: NEW_ADDITION_LABELS[slot]
        });
        occupied.add(slot);
      }
    });
  });
  return additions;
}

function resolveKeptBlower(
  positions: CompatibilityPosition[],
  components: ExistingSystemComponentType[]
): KeptBlower | null {
  const keptBlowerPosition =
    positions.find(
      (position) => position.type === 'blower' && !position.replacing
    ) ?? null;
  if (!keptBlowerPosition) return null;
  const keptBlowerComponent =
    components.find(
      (component) =>
        component.uniqueId ===
        keptBlowerPosition.existingEquipmentComponentUniqueId
    ) ?? null;
  return {
    label: keptBlowerPosition.label,
    heatingCapacityBtu: readComponentHeatingCapacity(keptBlowerComponent)
  };
}

export function planCompatibilityPositions(
  components: ExistingSystemComponentType[],
  combos: SlotOccupancy[]
): CompatibilityPositionPlan {
  const fromComponents = positionsFromComponents(components);
  const positions = [
    ...fromComponents,
    ...newAdditionPositions(fromComponents, combos)
  ];
  return {
    positions,
    keptComponentLabels: positions
      .filter((position) => !position.replacing)
      .map((position) => position.label),
    keptBlower: resolveKeptBlower(positions, components),
    keptSlots: keptEquipmentPositionSlots(components)
  };
}
